import { Link } from "react-router-dom";
import { Shield, Cloud, Server, Monitor, Lock, Camera, ArrowRight, CheckCircle } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const services = [
  {
    icon: Monitor,
    title: "IT Services",
    desc: "End-to-end information technology solutions designed to optimize your business operations and drive digital transformation.",
    features: [
      "Custom Software Development",
      "Application Management & Support",
      "IT Consulting & Strategy",
      "System Integration",
      "Database Management",
      "Help Desk & Technical Support",
    ],
    image: "https://images.pexels.com/photos/5203849/pexels-photo-5203849.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    icon: Server,
    title: "ERP Services",
    desc: "Streamline your business processes with our enterprise resource planning solutions that integrate all aspects of your operations.",
    features: [
      "ERP Implementation & Deployment",
      "Custom Module Development",
      "Data Migration & Integration",
      "Training & Change Management",
      "Ongoing Maintenance & Support",
      "Business Process Optimization",
    ],
    image: "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3Nzc1NDIzMjR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Cloud,
    title: "Cloud Services",
    desc: "Leverage the power of cloud computing with our comprehensive migration, management, and optimization services.",
    features: [
      "Cloud Migration & Strategy",
      "Multi-Cloud Management",
      "Cloud Security & Compliance",
      "Managed Hosting Solutions",
      "Disaster Recovery & Backup",
      "Performance Optimization",
    ],
    image: "https://images.unsplash.com/photo-1639066648921-82d4500abf1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxjbG91ZCUyMGNvbXB1dGluZyUyMHNlcnZlcnMlMjBkYXJrfGVufDB8fHx8MTc3NzU0MjMzOXww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Shield,
    title: "IT Infrastructure",
    desc: "Build a robust and reliable technology foundation with our infrastructure design, deployment, and management services.",
    features: [
      "Network Design & Implementation",
      "Server Setup & Management",
      "Data Center Solutions",
      "Hardware Procurement",
      "Structured Cabling",
      "Infrastructure Monitoring",
    ],
    image: "https://images.pexels.com/photos/5203849/pexels-photo-5203849.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    icon: Lock,
    title: "Cyber Security",
    desc: "Protect your business from evolving digital threats with our comprehensive cyber security solutions and expertise.",
    features: [
      "Threat Assessment & Penetration Testing",
      "Security Operations Center (SOC)",
      "Endpoint Protection",
      "Identity & Access Management",
      "Compliance & Regulatory Support",
      "Security Awareness Training",
    ],
    image: "https://images.unsplash.com/photo-1768839721176-2fa91fdce725?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxjeWJlciUyMHNlY3VyaXR5JTIwbG9jayUyMGJsdWV8ZW58MHx8fHwxNzc3NTQyMzM5fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Camera,
    title: "Security & Surveillance",
    desc: "Comprehensive physical security solutions including CCTV, access control, and integrated monitoring systems.",
    features: [
      "CCTV Installation & Monitoring",
      "Access Control Systems",
      "Biometric Solutions",
      "Video Analytics & AI",
      "Alarm & Intrusion Detection",
      "Remote Surveillance Management",
    ],
    image: "https://images.pexels.com/photos/5203849/pexels-photo-5203849.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function ServicesPage() {
  usePageMeta("Services", "Explore our IT Services, ERP Solutions, Cloud Services, IT Infrastructure, Cyber Security, and Security & Surveillance solutions for businesses.");
  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A122A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-4">
            Our Services
          </p>
          <h1 className="text-5xl sm:text-6xl tracking-tight leading-none font-['Outfit'] font-bold text-white mb-6">
            Enterprise-Grade Solutions
          </h1>
          <p className="text-[#94A3B8] max-w-2xl leading-relaxed">
            We offer a comprehensive suite of IT services designed to help businesses modernize, secure, and scale their technology operations. Explore our solutions below.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="space-y-20">
            {services.map((service, i) => (
              <div
                key={i}
                data-testid={`service-detail-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  i % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={`${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#111D3D] rounded-md">
                      <service.icon size={20} className="text-[#00E5FF]" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-white">
                      {service.title}
                    </h2>
                  </div>
                  <p className="text-[#94A3B8] leading-relaxed mb-6">{service.desc}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    {service.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-[#00E5FF] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#94A3B8]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-[#00E5FF] text-sm font-medium hover:text-[#33EEFF] transition-colors duration-300"
                  >
                    Enquire About {service.title} <ArrowRight size={16} />
                  </Link>
                </div>
                <div className={`${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-md border border-white/8 w-full h-72 object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A122A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-['Outfit'] font-bold text-white mb-4">
            Need a Custom Solution?
          </h2>
          <p className="text-[#94A3B8] max-w-xl mx-auto mb-10">
            Every business is unique. Let us design a tailored technology solution that fits your specific requirements.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-button"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5FF] text-[#060B19] font-semibold text-sm rounded-sm hover:bg-[#33EEFF] transition-all duration-300 btn-glow"
          >
            Request a Consultation <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
